/**
 * Size-budgeted context packing for schema_v2 draft generation.
 * Packs discrete units into chunks until a character budget is full, then starts
 * the next chunk — not fixed “one station per call”.
 */

export function clipText(value, max) {
  const s = String(value ?? '');
  if (s.length <= max) return s;
  return `${s.slice(0, Math.max(0, max - 1))}…`;
}

export function measureJson(value) {
  return JSON.stringify(value).length;
}

/**
 * @typedef {{ id: string, kind: string, priority: number, payload: object }} ContextUnit
 */

/**
 * Build discrete units from variable triangulation layers (highest priority first).
 * @returns {ContextUnit[]}
 */
export function buildDraftContextUnits({
  stations = {},
  claims = [],
  softEvidence = [],
  files = [],
  answers = {},
  narrativeMax = 1600,
  claimTextMax = 320,
  softTextMax = 320,
  fileSummaryMax = 220,
  answerValueMax = 400,
} = {}) {
  const units = [];

  Object.entries(stations || {}).forEach(([stationId, s]) => {
    const narrative = clipText(s?.narrative || '', narrativeMax);
    if (!narrative && s?.selectedLevel == null) return;
    const payload = {
      kind: 'sef_station',
      stationId,
      selectedLevel: s?.selectedLevel ?? null,
      narrative,
    };
    units.push({
      id: `station:${stationId}`,
      kind: 'sef_station',
      priority: 10,
      payload,
      size: measureJson(payload),
    });
  });

  (Array.isArray(claims) ? claims : []).forEach((c, i) => {
    const payload = {
      kind: 'claim',
      id: c.id || `claim_${i}`,
      stationId: c.stationId || null,
      claim: clipText(c.claim || '', claimTextMax),
      status: c.status || null,
      linkedFileNames: Array.isArray(c.linkedFileNames) ? c.linkedFileNames.slice(0, 5) : [],
      linkedFieldLabels: Array.isArray(c.linkedFieldLabels)
        ? c.linkedFieldLabels.slice(0, 5).map((x) => clipText(x, 80))
        : [],
      notes: clipText(c.notes || '', 160),
    };
    units.push({
      id: `claim:${payload.id}`,
      kind: 'claim',
      priority: 20,
      payload,
      size: measureJson(payload),
    });
  });

  (Array.isArray(softEvidence) ? softEvidence : []).forEach((s, i) => {
    const payload = {
      kind: 'soft_evidence',
      id: s.id || `soft_${i}`,
      stationId: s.stationId || null,
      text: clipText(s.text || '', softTextMax),
      source: s.source || 'sef_narrative',
      backed_by_upload: s.backed_by_upload === true,
    };
    units.push({
      id: `soft:${payload.id}`,
      kind: 'soft_evidence',
      priority: 30,
      payload,
      size: measureJson(payload),
    });
  });

  const fileList = Array.isArray(files) ? files : [];
  const extracted = fileList.filter((f) => f.status === 'extracted' || f.status === 'done');
  const failed = fileList.filter((f) => f.status === 'failed');
  [...extracted, ...failed].forEach((f, i) => {
    const payload = {
      kind: 'file_quality',
      file: f.name,
      status: f.status,
      score: f.quality?.score ?? null,
      worthiness: f.quality?.worthiness || null,
      summary: clipText(f.quality?.summary || f.detail || f.error || '', fileSummaryMax),
    };
    units.push({
      id: `file:${f.name || i}`,
      kind: 'file_quality',
      priority: f.status === 'failed' ? 35 : 40,
      payload,
      size: measureJson(payload),
    });
  });

  Object.entries(answers || {}).forEach(([key, value]) => {
    let v = value;
    if (typeof v === 'string') v = clipText(v, answerValueMax);
    else if (v != null && typeof v === 'object') {
      const j = JSON.stringify(v);
      v = j.length <= answerValueMax ? v : clipText(j, answerValueMax);
    }
    const payload = { kind: 'registration_answer', key, value: v };
    units.push({
      id: `answer:${key}`,
      kind: 'registration_answer',
      priority: 50,
      payload,
      size: measureJson(payload),
    });
  });

  return units.sort((a, b) => a.priority - b.priority || a.id.localeCompare(b.id));
}

/**
 * Greedy pack: fill each chunk up to budgetChars, then open a new chunk.
 * Oversized single units are truncated into the chunk alone.
 *
 * @param {ContextUnit[]} units
 * @param {number} budgetChars - max JSON size of payloads[] in a chunk
 * @returns {{ chunks: Array<{ index: number, units: ContextUnit[], size: number }>, totalUnits: number, dropped: number }}
 */
export function packUnitsIntoSizeChunks(units, budgetChars) {
  const budget = Math.max(2000, Number(budgetChars) || 50000);
  const list = Array.isArray(units) ? units : [];
  const chunks = [];
  let current = [];
  let currentSize = 2; // []

  const flush = () => {
    if (!current.length) return;
    chunks.push({
      index: chunks.length,
      units: current,
      size: currentSize,
    });
    current = [];
    currentSize = 2;
  };

  let dropped = 0;
  list.forEach((unit) => {
    let size = Number(unit.size) || measureJson(unit.payload);
    let payload = unit.payload;
    if (size > budget) {
      // Keep the unit but clip its largest string fields so one unit can occupy a chunk.
      const clipped = { ...payload };
      Object.keys(clipped).forEach((k) => {
        if (typeof clipped[k] === 'string') {
          clipped[k] = clipText(clipped[k], Math.max(200, Math.floor(budget / 2)));
        }
      });
      payload = clipped;
      size = measureJson(payload);
      if (size > budget) {
        dropped += 1;
        return;
      }
    }

    const nextSize = current.length === 0 ? size + 2 : currentSize + 1 + size; // comma
    if (current.length > 0 && nextSize > budget) {
      flush();
    }
    current.push({ ...unit, payload, size });
    currentSize = current.length === 1 ? size + 2 : currentSize + 1 + size;
  });
  flush();

  return { chunks, totalUnits: list.length, dropped };
}

export function chunkPayloads(chunk) {
  return (chunk?.units || []).map((u) => u.payload);
}
